import { ValueFormatting, ValueRoundingOptions, ValueType } from '../types/value';
import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from './chartDefaults';

export const EN_DASH = '–';

export const formatNegativeNumberString = (value: string) => {
  return `${value.replace('-', '(')})`;
};

export const isInvalidValue = (value: unknown) => {
  const isEmpty = `${value}`.trim().length === 0;
  return value === undefined || value === null || isNaN(Number(value)) || isEmpty;
};

export const roundToDigits = (value: number, digit: number) => {
  const roundedValue = Math.round(value / digit) * digit;
  // To prevent negative zero
  if (1 / roundedValue === -Infinity || 1 / roundedValue === Infinity) {
    return Math.abs(roundedValue);
  }
  return roundedValue;
};

// Return info that we need for formatting
export const roundingByOption = (value: number, formattingOption: number) => {
  let roundedValue = value;
  let maximumFractionDigits = 0;
  if (formattingOption < 0) {
    // Gotta be decimals
    maximumFractionDigits = Math.abs(formattingOption);
  } else {
    roundedValue = roundToDigits(value, 10 ** formattingOption);
  }

  return {
    maximumFractionDigits: maximumFractionDigits,
    roundedValue: roundedValue
  };
};

export const formatCurrency = (
  val: number,
  currentLocale: string,
  formattingOption: number = ValueRoundingOptions.Ones,
  display: ValueFormatting['negativeNumberDisplay'] = ValueRoundingOptions.Standard
): string => {
  if (isInvalidValue(val)) {
    return EN_DASH;
  }

  const roundingInfo = roundingByOption(val, formattingOption);
  const currencyFormatter = new Intl.NumberFormat(currentLocale, {
    style: 'currency',
    currency: DEFAULT_CURRENCY,
    maximumFractionDigits: roundingInfo.maximumFractionDigits,
    // Required for currency, or the default value will be set to 2 for minimumFractionDigit, this could cause out of range
    ...(roundingInfo.maximumFractionDigits <= 2 && { minimumFractionDigits: roundingInfo.maximumFractionDigits }),
    ...(display === ValueRoundingOptions.Accounting && {
      currencySign: 'accounting'
    })
  });

  return currencyFormatter.format(roundingInfo.roundedValue);
};

export const formatNumber = (
  val: number,
  currentLocale: string,
  formattingOption: number = ValueRoundingOptions.Ones,
  display: ValueFormatting['negativeNumberDisplay'] = ValueRoundingOptions.Standard
): string => {
  if (isInvalidValue(val)) {
    return EN_DASH;
  }
  const roundingInfo = roundingByOption(val, formattingOption);
  const numberFormatter = new Intl.NumberFormat(currentLocale, {
    style: 'decimal',
    maximumFractionDigits: roundingInfo.maximumFractionDigits,
    // Forcing decimal digits to prevent ending 0 get thrown
    ...(roundingInfo.maximumFractionDigits <= 2 && { minimumFractionDigits: roundingInfo.maximumFractionDigits })
  });
  const displayInAccountingNegative = display === ValueRoundingOptions.Accounting && roundingInfo.roundedValue < 0;
  if (displayInAccountingNegative) {
    return formatNegativeNumberString(numberFormatter.format(roundingInfo.roundedValue));
  }

  return numberFormatter.format(roundingInfo.roundedValue);
};

export const formatPercent = (
  val: number,
  currentLocale: string,
  formattingOption: number = ValueRoundingOptions.Ones,
  display: ValueFormatting['negativeNumberDisplay'] = ValueRoundingOptions.Standard
): string => {
  if (isInvalidValue(val)) {
    return EN_DASH;
  }
  // We need to times 100 for rounding, then divide 100 to apply percent style
  const roundingInfo = roundingByOption(val * 100, formattingOption);
  const isWholeNumber = val % 1 === 0;
  const percentFormatter = new Intl.NumberFormat(currentLocale, {
    style: 'percent',
    maximumFractionDigits: isWholeNumber ? 0 : roundingInfo.maximumFractionDigits,
    // Forcing decimal digits to prevent ending 0 get thrown, for whole numbers we trail the decimals
    ...(roundingInfo.maximumFractionDigits <= 2 &&
      !isWholeNumber && { minimumFractionDigits: roundingInfo.maximumFractionDigits })
  });

  const displayInAccountingNegative = display === ValueRoundingOptions.Accounting && roundingInfo.roundedValue < 0;
  if (displayInAccountingNegative) {
    return formatNegativeNumberString(percentFormatter.format(roundingInfo.roundedValue / 100));
  }

  return percentFormatter.format(roundingInfo.roundedValue / 100);
};

export const valueFormatter = (formattingOptions?: ValueFormatting, currentLocale: string = DEFAULT_LOCALE) => {
  const display = formattingOptions?.negativeNumberDisplay;
  return {
    currency: (val: number) =>
      formatCurrency(val, currentLocale, formattingOptions?.currencyRounding as number, display),
    number: (val: number) => formatNumber(val, currentLocale, formattingOptions?.numberRounding as number, display),
    percent: (val: number) => formatPercent(val, currentLocale, formattingOptions?.percentRounding as number, display)
  };
};

export const formatValue = (formatting: ValueFormatting, value: number, format?: ValueType): string => {
  switch (format) {
    case 'currency': {
      return valueFormatter(formatting).currency(value);
    }
    case 'percentage': {
      return valueFormatter(formatting).percent(value);
    }
    case 'numeric': {
      return valueFormatter(formatting).number(value);
    }
    default:
      return value.toString();
  }
};
